import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useMutation } from "@tanstack/react-query";
import { post, put } from "@rails/request.js";
import { toast } from "sonner";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { removeSection } from "@/lib/api";

type SectionFormProps = {
  siteSlug: string;
  onSectionCreated: () => void;
  onClose: () => void;
  onDeleted?: () => void;
  section?: {
    id: number;
    title: string;
    show_in_menu: boolean;
    display: boolean;
    list_or_project: string;
    description: string;
    [key: string]: any;
  };
};

const SectionForm: React.FC<SectionFormProps> = ({ siteSlug, onSectionCreated, onClose, onDeleted, section }) => {
  const [form, setForm] = useState({
    title: section?.title || "",
    show_in_menu: section?.show_in_menu ?? true,
    display: section?.display ?? false,
    list_or_project: section?.list_or_project || "list",
    description: section?.description || "",
  });


  // removed useToast, using sonner's toast directly

  React.useEffect(() => {
    if (section) {
      setForm({
        title: section.title || "",
        show_in_menu: section.show_in_menu ?? true,
        display: section.display ?? false,
        list_or_project: section.list_or_project || "list",
        description: section.description || "",
      });
    } else {
      // Reset form for new section
      setForm({
        title: "",
        show_in_menu: true,
        display: false,
        list_or_project: "list",
        description: "",
      });
    }
  }, [section]);

  const mutation = useMutation({
    mutationFn: async (params: any) => {
      if (section) {
        // Edit mode
        const response = await put(`/sites/${siteSlug}/sections/${section.id}.json`, {
          body: JSON.stringify({ section: params }),
          responseKind: "json",
        });
        if (!response.ok) {
          const error = await response.json;
          throw new Error(error.errors ? error.errors.join(", ") : "Failed to update section");
        }
        return response.json;
      } else {
        // Create mode
        const response = await post(`/sites/${siteSlug}/sections.json`, {
          body: JSON.stringify({ section: params }),
          responseKind: "json",
        });
        if (!response.ok) {
          const error = await response.json;
          throw new Error(error.errors ? error.errors.join(", ") : "Failed to create section");
        }
        return response.json;
      }
    },
    onSuccess: () => {
      if (!section) {
        // Show toast before any state changes
        toast("Section created", {
          description: "The section was created successfully.",
          action: {
            label: "Undo",
            onClick: () => {
              // Optionally implement undo logic here
            },
          },
        });
        setForm({
          title: "",
          show_in_menu: true,
          display: false,
          list_or_project: "list",
          description: "",
        });
        onClose();
        onSectionCreated();
      } else {
        // Show toast before any state changes
        toast("Section updated", {
          description: "The section was updated successfully.",
          action: {
            label: "Undo",
            onClick: () => {
              // Optionally implement undo logic here
            },
          },
        });
        onSectionCreated();
      }
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    let checked = false;
    if (type === "checkbox" && "checked" in e.target) {
      checked = (e.target as HTMLInputElement).checked;
    }
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  return (
    <div className="px-2">
      <div className="overflow-auto h-[calc(100vh-72px)]">
        <form
          onSubmit={e => {
            e.preventDefault();
            mutation.mutate(form);
          }}
          className="space-y-4"
        >
          <div>
            <Label htmlFor="title">Title</Label>
            <Input id="title" name="title" value={form.title} onChange={handleChange} required />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="display" name="display" checked={form.display} onCheckedChange={checked => setForm(f => ({ ...f, display: !!checked }))} />
            <Label htmlFor="display">Display</Label>
            <span className="text-xs text-muted-foreground ml-2">section visibility</span>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="show_in_menu" name="show_in_menu" checked={form.show_in_menu} onCheckedChange={checked => setForm(f => ({ ...f, show_in_menu: !!checked }))} />
            <Label htmlFor="show_in_menu">Show in menu</Label>
            <span className="text-xs text-muted-foreground ml-2">section displayed in menu</span>
          </div>
          <div>
            <Label htmlFor="list_or_project">List or project</Label>
            <Select
              value={form.list_or_project}
              onValueChange={value => setForm(f => ({ ...f, list_or_project: value }))}
              name="list_or_project"
            >
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="list">list</SelectItem>
                <SelectItem value="project">project</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground">what the index view should show? an index of the section's projects or the first project?</span>
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea id="description" name="description" value={form.description} onChange={handleChange} />
          </div>
          {mutation.isError && (
            <Alert variant="destructive" className="mb-2">
              {(mutation.error as Error).message}
            </Alert>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending
                ? section ? "Saving..." : "Creating..."
                : section ? "Save Changes" : "Create Section"}
            </Button>
            {section && (
              <>
                <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                  <AlertDialogTrigger asChild>
                    <Button
                      type="button"
                      variant="destructive"
                      disabled={deleting}
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Section</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this section? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel
                        disabled={deleting}
                        onClick={() => setShowDeleteDialog(false)}
                      >
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        disabled={deleting}
                        onClick={async () => {
                          setDeleting(true);
                          try {
                            await removeSection(siteSlug, section.id);
                            toast("Section deleted", {
                              description: "The section was deleted successfully.",
                            });
                            setShowDeleteDialog(false);
                            if (onDeleted) onDeleted();
                            else onClose();
                          } catch (err: any) {
                            toast("Delete failed", {
                              description: err?.message || "Failed to delete section.",
                            });
                          } finally {
                            setDeleting(false);
                          }
                        }}
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SectionForm;
